import { useState } from 'react';
import { Card, CardBody, Table, Input, Button, Badge, Alert, Spinner } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useInisNodes, useAssignInisNode, useDeassignInisNode } from './useNexusInis';

/**
 * INIS-Knotenverwaltung (#226). Selbst-registrierte Server (Enrollment über
 * Bootstrap-Secret) erscheinen hier als `pending` und bekommen Rolle + Ziel-
 * Hostname zugewiesen (schreibt DNS-TXT, NetBox, Zabbix-Host). Die abgeleitete
 * Identität (Adresse <hw_uuid>.node.valitype.net, Login nv-…, SSH-Port) berechnet
 * der Server aus product_uuid + Installations-ID.
 */
function fmt(s) {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE');
}

function statusBadge(s) {
  if (s === 'assigned') return <Badge color="success">assigned</Badge>;
  if (s === 'pending') return <Badge color="warning">pending</Badge>;
  return <Badge color="secondary">{s || '—'}</Badge>;
}

export function NexusInisPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const nodesQ = useInisNodes();
  const assign = useAssignInisNode();
  const deassign = useDeassignInisNode();
  const [draft, setDraft] = useState({}); // hardwareUuid → { role, targetHostname }

  const nodes = nodesQ.data?.nodes ?? nodesQ.data ?? [];
  const setField = (uuid, k, v) => setDraft((d) => ({ ...d, [uuid]: { ...d[uuid], [k]: v } }));

  return (
    <div>
      <PageHeader title="Nexus · INIS-Knoten" subtitle="Zero-Touch-Provisioning (#226) — Enrollment + Rollenzuweisung" />
      <Alert color="info" className="small">
        Server registrieren sich beim ersten Boot selbst und erscheinen als <strong>pending</strong>.
        Weise Rolle + Ziel-Hostname zu — das setzt DNS-TXT, NetBox und einen Zabbix-Host.
        Adresse/Login/SSH-Port sind aus Hardware- und Installations-ID abgeleitet.
      </Alert>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted small">{nodes.length} Knoten</span>
        <Button size="sm" color="secondary" outline disabled={nodesQ.isFetching} onClick={() => nodesQ.refetch()}>
          {nodesQ.isFetching ? 'Aktualisiere…' : 'Aktualisieren'}
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {nodesQ.isLoading ? (
            <div className="p-4 text-center"><Spinner size="sm" /> Lade Knoten…</div>
          ) : nodesQ.isError ? (
            <Alert color="danger" className="m-3 small">Konnte Knoten nicht laden: {String(nodesQ.error?.message || nodesQ.error)}</Alert>
          ) : nodes.length === 0 ? (
            <div className="p-4 text-center text-muted">Noch keine Knoten registriert.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle small">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Adresse (fqdn)</th>
                  <th>Login</th>
                  <th>Rolle / Ziel-Hostname</th>
                  <th>OS</th>
                  <th>Zuletzt</th>
                  <th className="text-end">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {nodes.map((n) => {
                  const d = draft[n.hardwareUuid] || {};
                  return (
                    <tr key={n.hardwareUuid || n.id}>
                      <td>{statusBadge(n.status)}</td>
                      <td><code className="small">{n.fqdn || n.hwUuid || n.hardwareUuid}</code></td>
                      <td>{n.username ? <><code>{n.username}</code>{n.sshPort ? <span className="text-muted"> :{n.sshPort}</span> : null}</> : '—'}</td>
                      <td>
                        {n.status === 'assigned' ? (
                          <span>{n.role || '—'} <span className="text-muted">/ {n.targetHostname || '—'}</span></span>
                        ) : (
                          <div className="d-flex gap-1">
                            <Input bsSize="sm" style={{ maxWidth: 120 }} placeholder="Rolle (k3s/web)"
                              value={d.role || ''} onChange={(e) => setField(n.hardwareUuid, 'role', e.target.value)} disabled={!canWrite} />
                            <Input bsSize="sm" style={{ maxWidth: 220 }} placeholder="node07.k3s.valitype.net"
                              value={d.targetHostname || ''} onChange={(e) => setField(n.hardwareUuid, 'targetHostname', e.target.value)} disabled={!canWrite} />
                          </div>
                        )}
                      </td>
                      <td className="text-muted">{n.os || '—'}{n.arch ? ` / ${n.arch}` : ''}</td>
                      <td className="text-muted">{fmt(n.lastSeenAt)}</td>
                      <td className="text-end">
                        {n.status === 'assigned' ? (
                          <Button size="sm" color="danger" outline disabled={!canWrite || deassign.isPending}
                            onClick={() => deassign.mutate({ hardwareUuid: n.hardwareUuid })}>
                            Aufheben
                          </Button>
                        ) : (
                          <Button size="sm" color="primary" disabled={!canWrite || assign.isPending || !d.role || !d.targetHostname}
                            onClick={() => assign.mutate({ hardwareUuid: n.hardwareUuid, role: d.role, targetHostname: d.targetHostname })}>
                            Zuweisen
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
